import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useParams, useSearchParams } from 'react-router-dom';
import PageContainer from '@shared/components/container/PageContainer';
import { RoomContext } from '@livekit/components-react';
import { useCallControllerGetCallByIdQuery } from '@shared/services/apiService/apiService';
import { useConversationMessageListControllerGetConversationsQuery } from '@shared/services/conversationApiService/apiService';
import { actions } from '@shared/store/ducks';
import { selectors } from '@shared/store/ducks/auth';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { ConversationContent } from '@widgets/Popup/MonitorCampaignV2/components';
import { CallActors } from '@shared/constants/recipient-status.constants';
import type { CallDto } from '@shared/services/apiService/apiService';
import type { DocMessage } from '@features/Admin/Campaign/model/type';
import { downloadDialogAsPdf } from '@features/Admin/Campaign/utils/js-pdf.util';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import { Room } from 'livekit-client';
import { CALL_DETAIL_RELATIONS, CALL_DETAIL_TABS, parseCallDetailTab } from '@features/CallDetail';
import { AnalysisTab, CallDetailHeader, RecordingSlot, TechnicalDetails } from './components';
import type { RecordingSlotHandle } from './components/RecordingSlot';
import { useStyles } from './styles';

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error)) return undefined;
  return typeof error.status === 'number' ? error.status : undefined;
};

interface CallDetailConversationProps {
  call: CallDto;
  phoneNumber: string;
  playbackPositionSeconds: number;
  recordingDurationSeconds: number;
  onSeekToRecordingPosition: (seconds: number) => void;
  isVisible: boolean;
}

const CallDetailRoomProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [room] = useState(() => new Room());

  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  return <RoomContext.Provider value={room}>{children}</RoomContext.Provider>;
};

const CallDetailConversation: React.FC<CallDetailConversationProps> = ({
  call,
  phoneNumber,
  playbackPositionSeconds,
  recordingDurationSeconds,
  onSeekToRecordingPosition,
  isVisible,
}) => {
  return (
    <CallDetailRoomProvider>
      <ConversationContent
        call={call}
        phoneNumber={phoneNumber}
        isRoomConnected={false}
        playbackPositionSeconds={playbackPositionSeconds}
        recordingDurationSeconds={recordingDurationSeconds}
        onSeekToRecordingPosition={onSeekToRecordingPosition}
        showCallSummary={false}
        fitAvailableHeight
        hideDownloadButton
        scrollToBottom={false}
        isVisible={isVisible}
      />
    </CallDetailRoomProvider>
  );
};

export const CallDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectors.getUser);
  const currentCompany = useAppSelector(selectors.getAuthenticatedUserCompany);
  const location = useLocation();
  const { campaignId, callId } = useParams<{ campaignId: string; callId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseCallDetailTab(searchParams.get('tab'));
  const isCallDetailRoute = Boolean(
    matchPath({ path: AuthenticatedPath.CALL_DETAIL, end: true }, location.pathname),
  );

  useEffect(() => {
    if (!isCallDetailRoute) return;

    const requestedTab = searchParams.get('tab');
    const isSupportedTab =
      requestedTab === CALL_DETAIL_TABS.TRANSCRIPT || requestedTab === CALL_DETAIL_TABS.ANALYSIS;

    if (requestedTab === null || isSupportedTab) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tab', CALL_DETAIL_TABS.TRANSCRIPT);
    setSearchParams(nextSearchParams, { replace: true });
  }, [isCallDetailRoute, searchParams, setSearchParams]);

  const recordingRef = useRef<RecordingSlotHandle | null>(null);
  const [playerState, setPlayerState] = useState({ currentTime: 0, duration: 0 });

  useEffect(() => {
    dispatch(actions.breadcrumbs.setIsShownBreadcrumbs(true));
    dispatch(
      actions.breadcrumbs.setBreadcrumb({
        pageTitle: 'campaignPage.callDetailTabs.title',
        titleValue: {},
        campaigntype: null,
        icon: null,
      }),
    );

    return () => {
      dispatch(actions.breadcrumbs.setIsShownBreadcrumbs(false));
    };
  }, [dispatch]);

  const { data, error, isError, isLoading, isFetching, refetch } =
    useCallControllerGetCallByIdQuery(
      { id: callId ?? '', relations: CALL_DETAIL_RELATIONS },
      { skip: !campaignId || !callId },
    );
  const callErrorStatus = getErrorStatus(error);
  const isForeignCompany = Boolean(
    data?.companyId && currentUser?.companyId && data.companyId !== currentUser.companyId,
  );
  const isCallNotFound =
    !campaignId ||
    !callId ||
    callErrorStatus === 403 ||
    callErrorStatus === 404 ||
    isForeignCompany;
  const { data: conversationDialog } = useConversationMessageListControllerGetConversationsQuery(
    { conversationId: data?.id ?? '' },
    { skip: !data?.id || isError || isForeignCompany },
  );
  const handlePlaybackTimeChange = useCallback((currentTime: number, duration: number) => {
    setPlayerState({ currentTime, duration });
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tab', value);
    setSearchParams(nextSearchParams);
  };

  const renderLoadingState = () => (
    <PageContainer title={t('campaignPage.callDetailTabs.title')}>
      <Stack spacing={2} className={classes.loadingContent}>
        <Paper variant="outlined" className={classes.loadingPaper}>
          <Stack spacing={1.5}>
            <Skeleton variant="text" width="35%" height={36} />
            <Skeleton variant="text" width="25%" />
            <Box className={classes.loadingGrid}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={44} />
              ))}
            </Box>
          </Stack>
        </Paper>
        <Skeleton variant="rounded" height={64} />
        <Skeleton variant="rounded" height={64} />
      </Stack>
    </PageContainer>
  );

  const renderNotFoundState = () => (
    <PageContainer
      title={t('campaignPage.callDetail.notFound.title')}
      companyName={currentCompany?.title}>
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
        }}>
        <Box sx={{ maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('campaignPage.callDetail.notFound.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('campaignPage.callDetail.notFound.description')}
          </Typography>
          <Button component={Link} to={AuthenticatedPath.CAMPAIGN} variant="contained">
            {t('campaignPage.callDetail.notFound.backToList')}
          </Button>
        </Box>
      </Box>
    </PageContainer>
  );

  if (!campaignId || !callId) {
    return renderNotFoundState();
  }

  if (isLoading) {
    return renderLoadingState();
  }

  if (isCallNotFound) {
    return renderNotFoundState();
  }

  if (isError || !data) {
    return (
      <PageContainer title={t('campaignPage.callDetailTabs.title')}>
        <Stack spacing={2} alignItems="flex-start">
          <Alert severity="error">{t('common.errorMessage')}</Alert>
          <Button variant="contained" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t('campaignPage.callDetailTabs.title')}>
      <Stack spacing={2} className={classes.pageContent}>
        <CallDetailHeader call={data} />
        <TechnicalDetails call={data} />
        <Box className={classes.recordingTabs}>
          <RecordingSlot
            ref={recordingRef}
            callId={data.id}
            hasRecording={Boolean(data.recordingUrl)}
            onPlaybackTimeChange={handlePlaybackTimeChange}
          />
          <Box className={classes.tabControls}>
            <Tabs
              className={classes.tabs}
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label={t('campaignPage.callDetailTabs.ariaLabel')}>
              <Tab
                value={CALL_DETAIL_TABS.TRANSCRIPT}
                label={t('campaignPage.callDetailTabs.transcript')}
                icon={<ChatBubbleOutlineIcon className={classes.tabIcon} />}
                iconPosition="start"
              />
              <Tab
                value={CALL_DETAIL_TABS.ANALYSIS}
                label={t('campaignPage.callDetailTabs.analysis')}
                icon={<AnalyticsOutlinedIcon className={classes.tabIcon} />}
                iconPosition="start"
              />
            </Tabs>
            {activeTab === CALL_DETAIL_TABS.TRANSCRIPT && (
              <Box className={classes.downloadButton}>
                <Tooltip title={t('campaignPage.transcript.download')} arrow>
                  <IconButton
                    disabled={!conversationDialog?.length}
                    onClick={() => {
                      if (!conversationDialog?.length) return;
                      downloadDialogAsPdf(
                        data.customer?.number || CallActors.CUSTOMER,
                        conversationDialog as DocMessage[],
                      );
                    }}
                    aria-label={t('campaignPage.transcript.download')}
                    size="small">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        <Box className={classes.tabPanels}>
          <Box className={cx(activeTab !== CALL_DETAIL_TABS.TRANSCRIPT && classes.hiddenTabPanel)}>
            <CallDetailConversation
              call={data}
              phoneNumber={data.customer?.number ?? ''}
              playbackPositionSeconds={playerState.currentTime}
              recordingDurationSeconds={playerState.duration}
              onSeekToRecordingPosition={(seconds) => recordingRef.current?.seekTo(seconds)}
              isVisible={activeTab === CALL_DETAIL_TABS.TRANSCRIPT}
            />
          </Box>
          <Box className={cx(activeTab !== CALL_DETAIL_TABS.ANALYSIS && classes.hiddenTabPanel)}>
            <AnalysisTab
              analysisStatus={data.analysisStatus}
              analysis={data.analysis}
              isLoading={isLoading}
              isError={isError}
              isFetching={isFetching}
              onRetry={async () => refetch()}
            />
          </Box>
        </Box>
      </Stack>
    </PageContainer>
  );
};
